import React, { useCallback, useEffect, useRef, useState } from "react"
import { Outlet, useNavigate } from "react-router-dom"
import { Layout, Button, Flex, Spin, Tooltip } from "antd"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import useSearchFilter from "hooks/useSearchFilter"
import moment from "moment"
import { useDispatch, useSelector } from "react-redux"
import { DATE_FORMAT } from "modules/datepicker/constants"
import styles from "./Calendar.module.css"
import Header from "./Header"
import CalendarViewSwitcher from "./CalendarViewSwitcher"
import {
  getCalendarBookings,
  getCalendarFilters,
  normalizeCalendarFilters,
  setSavedFilters,
  setCalendarBookingsLoading,
  updateCalendarFilters,
  updateCalendarFiltersAndSync,
  getCalendarEquipment,
  resetCalendarFiltersReady,
  markCalendarFiltersReady,
  setCalendarLastView
} from "store/calendar"
import { getCalendarBlackouts } from "store/blackouts"
import { DEFAULT_START_HOUR, DEFAULT_END_HOUR } from "./constants"
import { fetchExperiences } from "store/experiences"
import { getCalendarCrew } from "store/staff"
import { ExitScheduleTimeOff, Journal, ScheduleTimeOff } from "./Modals"
import DayDetailsPanel from "./DayDetailsPanel"
import AddBlackoutButton from "./Header/AddBlackoutButton"
import { useCalendarBlackouts } from "./hooks/useCalendarBlackouts"
import { useCalendarDateRange } from "./useCalendarDateRange"
import { useDevice } from "hooks/useDevice"
import { useMediaQuery } from "react-responsive"
import { useTranslation } from "react-i18next"
import { routes } from "router"
import useActiveGuide from "hooks/useActiveClient"
import { useStripeIssue } from "hooks/useStripeIssue"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import CustomDrawer from "ui/Drawer"
import { buildBookingsParams, buildBlackoutsParams } from "./utils"

const tabToViewType = (tab) => {
  if (tab === "my-bookings") return "my_bookings"
  if (tab === "crew") return "crew"
  if (tab === "equipment") return "equipment"
  return null
}

const viewTypeToTab = (vt) => {
  if (vt === "my_bookings") return "my-bookings"
  if (vt === "crew") return "crew"
  if (vt === "equipment") return "equipment"
  return "all-bookings"
}

const isValidCalendarDate = (date) => Boolean(date) && moment(date, DATE_FORMAT, true).isValid()
const CALENDAR_RANGES = ["date", "week", "month"]

const calendarViewToRange = (cv) => {
  if (cv === "day") return "date"
  if (cv === "week") return "week"
  return "month"
}

const getTabUnassigned = (tab, savedFilters) => {
  if (tab === "crew") return Boolean(savedFilters.crew_unassigned)
  if (tab === "equipment") return Boolean(savedFilters.equipment_unassigned)
  return false
}

function Calendar() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isMobile, isTablet, isDesktop } = useDevice()
  const isWideDesktop = useMediaQuery({ query: "(min-width: 1280px)" })
  const { t } = useTranslation()
  const { isActive: active } = useActiveGuide()
  const { hasPermission, hasFullAccessFlag } = usePermissions()
  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)
  const { hasStripeIssue, stripeIssueMessage } = useStripeIssue()
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const [actionsDrawerOpen, setActionsDrawerOpen] = useState(false)

  const savedFilters = useSelector((state) => state.calendar?.savedFilters)
  const filtersReady = useSelector((state) => state.calendar?.filtersReady || false)
  const isLoading = useSelector((state) => state.calendar?.loading || false)
  const hasMyBookings = useSelector((state) => state.calendar?.hasMyBookings)
  const lastView = useSelector((state) => state.calendar?.lastView)

  // Always-current ref — no stale closure issues
  const savedFiltersRef = useRef(savedFilters)
  savedFiltersRef.current = savedFilters
  const lastViewRef = useRef(lastView)
  lastViewRef.current = lastView
  // Where to reopen when the URL has no date: the last date / list view this session, else today
  const getRestoredView = useCallback(() => {
    const { date, isListView } = lastViewRef.current || {}
    if (!isValidCalendarDate(date)) return { date: moment().format(DATE_FORMAT) }
    return { date, ...(isListView ? { listview: true } : {}) }
  }, [])

  const prevTabRef = useRef(null)
  // Hydrate URL params from saved filters only once (on initial load). `filtersReady`
  // toggles on every tab/range switch (see the persist effect below), so without this
  // guard the hydration effect would re-fire and snap the tab back to the persisted value.
  const hasHydratedParamsRef = useRef(false)

  const [startHour, setStartHour] = useState(() => {
    if (savedFilters?.time_range_from) {
      const h = Number.parseInt(savedFilters.time_range_from.split(":")[0], 10)
      return Number.isNaN(h) ? DEFAULT_START_HOUR : h
    }
    return DEFAULT_START_HOUR
  })
  const [endHour, setEndHour] = useState(() => {
    if (savedFilters?.time_range_to) {
      const h = Number.parseInt(savedFilters.time_range_to.split(":")[0], 10)
      return Number.isNaN(h) ? DEFAULT_END_HOUR : h
    }
    return DEFAULT_END_HOUR
  })

  const [params, updateSearchFilters] = useSearchFilter({
    accessAttributes: ["range", "date", "agenda", "weather", "trip_id", "journal_date", "unavailable_id", "tab", "listview"]
  })

  // Refs for imperative bookings call inside async chains (avoids stale closures)
  const datesRef = useRef(null)
  const paramsRef = useRef(params)
  paramsRef.current = params
  // The URL as the Calendar was opened, before the Header writes its default range on mount
  const initialParamsRef = useRef(params)

  // Reset filtersReady on unmount so navigating back triggers a fresh fetch
  useEffect(() => {
    return () => {
      dispatch(resetCalendarFiltersReady())
    }
  }, [dispatch])

  // Fetch saved filters on mount - always refetch when component mounts
  useEffect(() => {
    dispatch(getCalendarFilters()).catch(() => {})
  }, [dispatch])

  // Update time range state when filters are loaded from API
  useEffect(() => {
    if (!filtersReady) return

    if (savedFilters?.time_range_from) {
      const h = Number.parseInt(savedFilters.time_range_from.split(":")[0], 10)
      if (!Number.isNaN(h)) {
        setStartHour(h)
      }
    }

    if (savedFilters?.time_range_to) {
      const h = Number.parseInt(savedFilters.time_range_to.split(":")[0], 10)
      if (!Number.isNaN(h)) {
        setEndHour(h)
      }
    }
  }, [filtersReady, savedFilters?.time_range_from, savedFilters?.time_range_to])

  // Once filters are ready, hydrate URL params from saved filters — but only on the initial
  // load. Re-running on later tab/range switches would overwrite the user's just-clicked tab
  // with the (possibly still-syncing) persisted value, making the tab visibly snap back.
  useEffect(() => {
    if (!filtersReady) return
    if (hasHydratedParamsRef.current) return
    const sf = savedFiltersRef.current
    const savedTab = viewTypeToTab(sf.view_type)
    // A link that opens a specific date keeps its own range; otherwise use the saved range
    const { date: linkDate, range: linkRange } = initialParamsRef.current
    const isDateLink = isValidCalendarDate(linkDate) && CALENDAR_RANGES.includes(linkRange)
    // A date in the URL (deep link) wins; otherwise reopen where the guide left off this session
    const nextParams = {
      range: isDateLink ? linkRange : calendarViewToRange(sf.calendar_view),
      ...(isValidCalendarDate(params.date) ? { date: params.date } : getRestoredView()),
      tab: savedTab,
      // null (not undefined) so useSearchFilter drops the param instead of writing "undefined"
      agenda: sf.agenda_view ? true : null
    }

    hasHydratedParamsRef.current = true

    const currentAgenda = params.agenda === true || params.agenda === "true"
    const nextAgenda = nextParams.agenda === true
    if (
      params.range === nextParams.range &&
      params.date === nextParams.date &&
      params.tab === nextParams.tab &&
      currentAgenda === nextAgenda &&
      !nextParams.listview
    ) {
      return
    }

    updateSearchFilters(nextParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady, savedFilters?.view_type, savedFilters?.calendar_view, savedFilters?.agenda_view])

  // Remember the date / week list view for the next visit. Waits for hydration so the
  // pre-hydration URL (no date yet) can't overwrite the remembered values.
  useEffect(() => {
    if (!hasHydratedParamsRef.current || !isValidCalendarDate(params.date)) return
    dispatch(setCalendarLastView({ date: params.date, isListView: params.listview === true || params.listview === "true" }))
  }, [filtersReady, params.date, params.listview, dispatch])

  // The day details panel is shared by every view, so its selected date lives here.
  const [panelDate, setPanelDate] = useState(null)
  const { days: blackoutDays } = useCalendarBlackouts()

  /**
   * The panel's ‹ › step a day at a time, but the blackout feed holds only the window the view
   * loaded — the one day in Day view, the grid in Week and Month. A step past that window moves
   * the calendar there too, so the feed follows the panel instead of reading the day as clear.
   * `YYYY-MM-DD` strings compare in date order.
   */
  const navigatePanel = useCallback(
    (nextDate) => {
      setPanelDate(nextDate)
      const d = datesRef.current
      const isLoaded = Boolean(d?.start && d?.end) && nextDate >= d.start.slice(0, 10) && nextDate <= d.end.slice(0, 10)
      if (!isLoaded) updateSearchFilters({ date: nextDate })
    },
    [updateSearchFilters]
  )
  const [modals, setModals] = useState({ scheduleTimeOff: false, exitScheduleTimeOff: false, journal: false })
  const setIsScheduleTimeOffModalOpen = (open) => setModals((m) => ({ ...m, scheduleTimeOff: open }))
  const setIsExitScheduleTimeOffModalOpen = (open) => setModals((m) => ({ ...m, exitScheduleTimeOff: open }))
  const setIsJournalModalOpen = (open) => setModals((m) => ({ ...m, journal: open }))

  // changeHandler: write single param change directly to URL
  const changeHandler = useCallback(
    ({ target, extra }) => {
      const update = {
        [target.name]: target.value,
        ...(extra && { [extra.name]: extra.value })
      }
      // Always ensure date is set. A missing date (the Header's default-range write on mount,
      // e.g. arriving from the sidebar) reopens the remembered view; switching range jumps to today.
      if (!params.date && target.name !== "date") {
        Object.assign(update, getRestoredView())
      } else if (target.name === "range") {
        update.date = moment().format(DATE_FORMAT)
      }
      updateSearchFilters(update)
    },
    [updateSearchFilters, params.date, getRestoredView]
  )

  // Handle filter changes from Header:
  // persist via update_filters, update local state, then call bookings with fresh filters
  const handleFiltersChange = useCallback(
    (newFilters) => {
      const normalized = normalizeCalendarFilters({
        ...savedFiltersRef.current,
        ...newFilters,
        calendar_view: params.range === "date" ? "day" : params.range || "month",
        view_type: tabToViewType(params.tab)
      })
      dispatch(setSavedFilters(normalized))
      dispatch(setCalendarBookingsLoading())
      dispatch(updateCalendarFilters(normalized))
        .then(() => {
          const d = datesRef.current
          const p = paramsRef.current
          if (d?.start && d?.end) {
            dispatch(getCalendarBookings(buildBookingsParams(p, d, normalized)))
            // The day chips are scoped by the same filters, so they refresh alongside the rows.
            dispatch(getCalendarBlackouts(buildBlackoutsParams(p, d, normalized)))
          }
        })
        .catch(() => {})
    },
    [dispatch, params.range, params.tab]
  )

  // On tab or range change: persist the new view_type / calendar_view to server.
  // Skip on first run (prevTab === null) to avoid a spurious update_filters on mount.
  useEffect(() => {
    if (!filtersReady) return
    if (!params.range || !params.tab) return
    const calendar_view = params.range === "date" ? "day" : params.range
    const view_type = tabToViewType(params.tab)
    const prevTab = prevTabRef.current
    prevTabRef.current = params.tab

    // First run — just record the tab, don't trigger any API calls
    if (prevTab === null) return

    if (savedFiltersRef.current.calendar_view === calendar_view && savedFiltersRef.current.view_type === view_type) return

    const updated = normalizeCalendarFilters({ ...savedFiltersRef.current, calendar_view, view_type })
    dispatch(setSavedFilters(updated))
    dispatch(resetCalendarFiltersReady())
    // On failure keep the optimistic savedFilters and just unblock the gate. Refetching here
    // would restore the server's stale view_type, which re-mismatches this effect's guard and
    // re-fires the PATCH endlessly (update_filters retry loop).
    dispatch(updateCalendarFiltersAndSync(updated)).catch(() => dispatch(markCalendarFiltersReady()))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.range, params.tab, filtersReady])

  const dates = useCalendarDateRange(params)
  datesRef.current = dates

  useEffect(() => {
    dispatch(getCalendarCrew())
    dispatch(fetchExperiences("calendar"))
    dispatch(getCalendarEquipment())
  }, [dispatch])

  // Bookings fire on date/range/tab changes.
  // Filter changes call bookings imperatively — savedFilters intentionally excluded from deps.
  // STRONG CHECK: Only call bookings API after filters are ready AND params are set
  useEffect(() => {
    // Wait for filters to be ready
    if (!filtersReady) return

    // Wait for dates to be available
    if (!dates?.start || !dates?.end) return

    // Wait for URL params to be set (range and tab must exist)
    if (!params.range || !params.tab) return

    // Ensure savedFilters has been populated
    if (!savedFiltersRef.current) return

    // All conditions met - safe to call bookings API
    dispatch(getCalendarBookings(buildBookingsParams(params, dates, savedFiltersRef.current)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady, dates?.start, dates?.end, params.range, params.tab, dispatch])

  /**
   * Blackouts follow the bookings: same date window, and on the Crew and Equipment tabs the same
   * resource filters, so a day's chip describes the rows underneath it. Reads through refs like
   * the imperative bookings call does — a filter change dispatches this before React has
   * re-rendered with the new `savedFilters`.
   */
  const refreshBlackouts = useCallback(() => {
    const d = datesRef.current
    if (!d?.start || !d?.end) return
    dispatch(getCalendarBlackouts(buildBlackoutsParams(paramsRef.current, d, savedFiltersRef.current || {})))
  }, [dispatch])

  // The tab and the resource filters change what the feed should return, not just when. Waits
  // for `filtersReady` for the same reason the bookings call does: the saved filters arrive
  // after mount, and a request sent before them asks for the unscoped month. Later filter
  // changes refresh through `handleFiltersChange`, which has the new filters in hand.
  useEffect(() => {
    if (!filtersReady) return
    if (!dates?.start || !dates?.end) return
    refreshBlackouts()
  }, [filtersReady, dates?.start, dates?.end, params.tab, refreshBlackouts])

  useEffect(() => {
    if (!dates?.start || !dates?.end) return
    const data = {}
    const startMoment = moment(dates.start)
    const endMoment = moment(dates.end)
    if (params.trip_id) data.trip_id = params.trip_id
    data.forecast_date = []
    while (startMoment.isSameOrBefore(endMoment)) {
      data.forecast_date.push(startMoment.format(DATE_FORMAT))
      startMoment.add(1, "day")
    }
  }, [dates?.start, dates?.end, params?.trip_id, params?.weather, dispatch])

  const handleCancelScheduleTimeOffModal = () => setIsExitScheduleTimeOffModalOpen(true)
  const handleJournalCancel = () => setIsJournalModalOpen(false)
  const handleCancelExitScheduleTimeOffModal = () => setIsExitScheduleTimeOffModalOpen(false)
  const handleYesExitScheduleTimeOffModal = () => {
    setIsExitScheduleTimeOffModalOpen(false)
    setIsScheduleTimeOffModalOpen(false)
  }
  const handleJournalClick = () => setIsJournalModalOpen(true)

  const pad = (n) => String(n).padStart(2, "0")

  return (
    <>
      {isLoading && (
        <div className={styles.calendarOverlay}>
          <Spin size="large" />
        </div>
      )}
      <Layout className={styles.container} id="dashboard">
        <div className={styles.header}>
          <Header
            params={params}
            onChangeFilters={changeHandler}
            handleJournalClick={handleJournalClick}
            setIsScheduleTimeOffModalOpen={setIsScheduleTimeOffModalOpen}
            onBlackoutCreated={refreshBlackouts}
            onFiltersChange={handleFiltersChange}
            startHour={startHour}
            endHour={endHour}
            onStartHourChange={(h) => {
              setStartHour(h)
              const updated = normalizeCalendarFilters({
                ...savedFiltersRef.current,
                time_range_from: `${pad(h)}:00`,
                time_range_to: `${pad(endHour)}:00`
              })
              dispatch(setSavedFilters(updated))
              dispatch(updateCalendarFilters(updated))
                .then(() => {
                  const d = datesRef.current
                  const p = paramsRef.current
                  if (d?.start && d?.end) dispatch(getCalendarBookings(buildBookingsParams(p, d, updated)))
                })
                .catch(() => {})
            }}
            onEndHourChange={(h) => {
              setEndHour(h)
              const updated = normalizeCalendarFilters({
                ...savedFiltersRef.current,
                time_range_from: `${pad(startHour)}:00`,
                time_range_to: `${pad(h)}:00`
              })
              dispatch(setSavedFilters(updated))
              dispatch(updateCalendarFilters(updated))
                .then(() => {
                  const d = datesRef.current
                  const p = paramsRef.current
                  if (d?.start && d?.end) dispatch(getCalendarBookings(buildBookingsParams(p, d, updated)))
                })
                .catch(() => {})
            }}
            onResetHours={() => {
              setStartHour(DEFAULT_START_HOUR)
              setEndHour(DEFAULT_END_HOUR)
              const updated = normalizeCalendarFilters({ ...savedFiltersRef.current, time_range_from: null, time_range_to: null })
              dispatch(setSavedFilters(updated))
              dispatch(updateCalendarFilters(updated))
                .then(() => {
                  const d = datesRef.current
                  const p = paramsRef.current
                  if (d?.start && d?.end) dispatch(getCalendarBookings(buildBookingsParams(p, d, updated)))
                })
                .catch(() => {})
            }}
            hasMyBookings={hasMyBookings}
            savedFilters={savedFilters}
          />
        </div>
        <div className={styles.calendarWrapper}>
          <div className={styles.calendar}>
            {(isMobile || isTablet) && (
              <div className={styles.mobileGoToToday}>
                <Button
                  color="primary"
                  type="default"
                  variant="outlined"
                  shape="round"
                  className={styles.goToToday}
                  disabled={moment(params.date).isSame(moment(), "day")}
                  onClick={() => changeHandler({ target: { name: "date", value: moment().format(DATE_FORMAT) } })}
                >
                  Go to Today
                </Button>
              </div>
            )}
            <CalendarViewSwitcher
              onChangeFilters={changeHandler}
              experienceIds={savedFilters.experience_ids || []}
              crewIds={[
                ...(savedFilters.crew_unassigned ? ["unassigned"] : []),
                ...(savedFilters.profile_ids || [])
              ]}
              equipmentIds={[
                ...(savedFilters.equipment_unassigned ? ["unassigned"] : []),
                ...(savedFilters.boat_ids || [])
              ]}
              categoryIds={savedFilters.category_ids || []}
              roleIds={savedFilters.role_ids || []}
              startHour={startHour}
              endHour={endHour}
              showTimeRangeSettings={params.range === "date" || (params.range === "week" && (params.agenda === true || params.agenda === "true"))}
              onStartHourChange={(h) => {
                setStartHour(h)
                const updated = normalizeCalendarFilters({
                  ...savedFiltersRef.current,
                  time_range_from: `${pad(h)}:00`,
                  time_range_to: `${pad(endHour)}:00`
                })
                dispatch(setSavedFilters(updated))
                dispatch(updateCalendarFilters(updated))
                  .then(() => {
                    const d = datesRef.current
                    const p = paramsRef.current
                    if (d?.start && d?.end) dispatch(getCalendarBookings(buildBookingsParams(p, d, updated)))
                  })
                  .catch(() => {})
              }}
              onEndHourChange={(h) => {
                setEndHour(h)
                const updated = normalizeCalendarFilters({
                  ...savedFiltersRef.current,
                  time_range_from: `${pad(startHour)}:00`,
                  time_range_to: `${pad(h)}:00`
                })
                dispatch(setSavedFilters(updated))
                dispatch(updateCalendarFilters(updated))
                  .then(() => {
                    const d = datesRef.current
                    const p = paramsRef.current
                    if (d?.start && d?.end) dispatch(getCalendarBookings(buildBookingsParams(p, d, updated)))
                  })
                  .catch(() => {})
              }}
              onResetHours={() => {
                setStartHour(DEFAULT_START_HOUR)
                setEndHour(DEFAULT_END_HOUR)
                const updated = normalizeCalendarFilters({ ...savedFiltersRef.current, time_range_from: null, time_range_to: null })
                dispatch(setSavedFilters(updated))
                dispatch(updateCalendarFilters(updated))
                  .then(() => {
                    const d = datesRef.current
                    const p = paramsRef.current
                    if (d?.start && d?.end) dispatch(getCalendarBookings(buildBookingsParams(p, d, updated)))
                  })
                  .catch(() => {})
              }}
              onOpenDayPanel={setPanelDate}
            />
          </div>
        </div>
        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
      </Layout>
      {modals.journal && (
        <Journal
          open={modals.journal}
          setOpen={setIsJournalModalOpen}
          onCancel={handleJournalCancel}
          selectedDate={params.date || moment().format(DATE_FORMAT)}
          startDate={dates?.start || params.date || moment().format(DATE_FORMAT)}
          endDate={dates?.end || params.date || moment().format(DATE_FORMAT)}
          range={params.range}
        />
      )}
      {modals.scheduleTimeOff && (
        <ScheduleTimeOff
          dates={dates}
          open={modals.scheduleTimeOff}
          onCancel={handleCancelScheduleTimeOffModal}
          setIsScheduleTimeOffModalOpen={setIsScheduleTimeOffModalOpen}
          onSuccess={() => {
            // Refresh bookings with proper filter parameters after time off is saved
            if (dates?.start && dates?.end) {
              dispatch(getCalendarBookings(buildBookingsParams(params, dates, savedFiltersRef.current)))
            }
          }}
        />
      )}
      {modals.exitScheduleTimeOff && (
        <ExitScheduleTimeOff
          open={modals.exitScheduleTimeOff}
          onCancel={handleCancelExitScheduleTimeOffModal}
          onYes={handleYesExitScheduleTimeOffModal}
        />
      )}
      {panelDate && (
        <DayDetailsPanel
          isOpen
          date={panelDate}
          dayBlackout={blackoutDays[panelDate] || null}
          onClose={() => setPanelDate(null)}
          onNavigate={navigatePanel}
          onChanged={refreshBlackouts}
        />
      )}
      {(isMobile || isTablet || (isDesktop && !isWideDesktop)) && (
        <>
          <div className={styles.fab}>
            <button type="button" className={styles.fabButton} onClick={() => setActionsDrawerOpen(true)} aria-label="Actions">
              +
            </button>
          </div>
          <CustomDrawer open={actionsDrawerOpen} setOpen={setActionsDrawerOpen} title="Actions">
            <Flex vertical gap={12}>
              {canCreateBooking && (
                <Tooltip title={hasStripeIssue ? stripeIssueMessage : ""}>
                  <span className={hasStripeIssue ? styles.disabledBtnBlockWrapper : undefined}>
                    <Button
                      block
                      shape="round"
                      size="large"
                      className={styles.actionSheetButton}
                      disabled={hasStripeIssue}
                      style={hasStripeIssue ? { pointerEvents: "none" } : undefined}
                      onClick={() => {
                        setActionsDrawerOpen(false)
                        if (active && !hasStripeIssue) navigate(routes.guideBookingNewPath())
                      }}
                    >
                      {t("bookings.add")}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {/* The guide/admin handles blackouts; crew and staff schedule their own time off */}
              {!hasFullAccessFlag && (params.tab === "my-bookings" || params.tab === "crew") && (
                <Button
                  block
                  shape="round"
                  size="large"
                  className={styles.actionSheetButton}
                  onClick={() => {
                    setActionsDrawerOpen(false)
                    if (active) setIsScheduleTimeOffModalOpen(true)
                  }}
                >
                  {t("unavailable_days.schedule_time_off")}
                </Button>
              )}
              {hasFullAccessFlag && (
                <AddBlackoutButton
                  isBlock
                  className={styles.actionSheetButton}
                  isDisabled={!active}
                  onScopeSelected={() => setActionsDrawerOpen(false)}
                  onCreated={refreshBlackouts}
                />
              )}
              {canCreateJournal && (
                <Button
                  block
                  shape="round"
                  size="large"
                  className={styles.actionSheetButton}
                  onClick={() => {
                    setActionsDrawerOpen(false)
                    if (active) handleJournalClick()
                  }}
                >
                  {t("calendar.journal.add_journal")}
                </Button>
              )}
            </Flex>
          </CustomDrawer>
        </>
      )}
    </>
  )
}

export default Calendar
